import { useState, useRef, useCallback, useEffect } from "react";
import { SplatViewer } from "./components/SplatViewer";
import { ControlsPanel } from "./components/ControlsPanel";
import { LoadingScreen } from "./components/LoadingScreen";
import { InfoPopup } from "./components/InfoPopup";
import { useSplatUrl } from "./hooks/useSplatUrl";
import { useSplatStitching } from "./hooks/useSplatStitching";
import type CameraControlsImpl from "camera-controls";
import "./index.css";

export function App() {
  const splatParam = useSplatUrl();
  const stitch = useSplatStitching();
  const [loading, setLoading] = useState(true);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const controlsRef = useRef<CameraControlsImpl>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Single-splat mode if ?splat= is set
  const hasSplatParam =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("splat");

  const handleCameraMove = useCallback(
    (pos: { x: number; y: number; z: number }) => {
      stitch.updateCamera(pos.x, pos.z);
    },
    [stitch.updateCamera],
  );

  const handleLoadingChange = useCallback(
    (isLoading: boolean) => {
      setLoading(isLoading);
      if (!isLoading) {
        setTimeout(() => setShowInfoPopup(true), 800);
      }
    },
    [],
  );

  const handleDismissInfo = useCallback(() => {
    setShowInfoPopup(false);
  }, []);

  const handleReset = useCallback(() => {
    controlsRef.current?.reset(true);
  }, []);

  // Reset camera and show title card when switching worlds
  useEffect(() => {
    controlsRef.current?.reset(true);
  }, [selectedIndex]);

  // Nav callbacks
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex < stitch.worlds.length - 1;
  const onPrev = useCallback(() => {
    setSelectedIndex((i) => {
      const next = Math.max(0, i - 1);
      if (next !== i) setShowInfoPopup(false);
      return next;
    });
  }, []);
  const onNext = useCallback(
    () => {
      setSelectedIndex((i) => {
        const next = Math.min(stitch.worlds.length - 1, i + 1);
        if (next !== i) setShowInfoPopup(false);
        return next;
      });
    },
    [stitch.worlds.length],
  );

  // In single-splat mode, build a one-element slots array from the URL param
  // In multi-splat mode, show only the selected world at origin
  const selectedSlot = stitch.slots[selectedIndex];
  const slots = hasSplatParam
    ? [{ id: "single", url: splatParam, offset: { x: 0, y: 0, z: 0 }, shouldLoad: true }]
    : selectedSlot
      ? [{ ...selectedSlot, offset: { x: 0, y: 0, z: 0 }, shouldLoad: true }]
      : [];

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-background">
      {slots.length > 0 && (
        <SplatViewer
          slots={slots}
          paused={showInfoPopup}
          onLoadingChange={handleLoadingChange}
          onCameraMove={handleCameraMove}
          controlsRef={controlsRef}
        />
      )}
      <LoadingScreen visible={loading} />
      <InfoPopup
        visible={showInfoPopup}
        onDismiss={handleDismissInfo}
        worldIndex={selectedIndex}
      />
      <ControlsPanel
        onReset={handleReset}
        nav={{
          currentIndex: selectedIndex,
          total: stitch.worlds.length,
          hasNext,
          hasPrev,
          onNext,
          onPrev,
        }}
      />
    </div>
  );
}

export default App;
